import type { TFunction } from 'i18next';
import type { U3202CapabilityKey, U3202ModelCapabilities } from './U3202CapabilitiesTypes';
import { u3202CapabilitySupported, u3202GetCapabilityLevels } from './u3202CapabilityHelpers';

export interface U3202CapabilityBadge {
  key: U3202CapabilityKey | 'max_tokens';
  label: string;
  detail?: string;
}

const CAPABILITY_KEYS: U3202CapabilityKey[] = [
  'streaming',
  'image_input',
  'pdf_input',
  'effort',
  'thinking',
  'structured_outputs',
];

/**
 * Liste les badges de capabilities à afficher pour une ligne modèle ou une fiche.
 */
export function u3202FormatCapabilitiesSummary(
  capabilities: U3202ModelCapabilities | null | undefined,
  t: TFunction,
  ns: string,
): U3202CapabilityBadge[] {
  if (!capabilities || Object.keys(capabilities).length === 0) {
    return [];
  }

  const badges: U3202CapabilityBadge[] = [];

  for (const key of CAPABILITY_KEYS) {
    if (!u3202CapabilitySupported(capabilities, key)) {
      continue;
    }
    let detail: string | undefined;
    if (key === 'effort') {
      const levels = u3202GetCapabilityLevels(capabilities, 'effort');
      if (levels?.length) {
        detail = levels.join(', ');
      }
    }
    if (key === 'thinking' && capabilities.thinking?.types) {
      const types = Object.entries(capabilities.thinking.types)
        .filter(([, enabled]) => enabled)
        .map(([name]) => name);
      if (types.length) {
        detail = types.join(', ');
      }
    }
    badges.push({
      key,
      label: t(`capability.${key}`, { ns }),
      detail,
    });
  }

  if (typeof capabilities.max_tokens === 'number') {
    badges.push({
      key: 'max_tokens',
      label: t('capability.max_tokens', { ns, count: capabilities.max_tokens }),
    });
  }

  return badges;
}

/**
 * Texte court pour une colonne de grille (badges séparés par ·).
 */
export function u3202FormatCapabilitiesSummaryText(
  capabilities: U3202ModelCapabilities | null | undefined,
  t: TFunction,
  ns: string,
): string {
  const badges = u3202FormatCapabilitiesSummary(capabilities, t, ns);
  if (badges.length === 0) {
    return t('capabilities.none', { ns });
  }
  return badges
    .map((badge) => (badge.detail ? `${badge.label} (${badge.detail})` : badge.label))
    .join(' · ');
}
